import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Camera, MapPin, X, Search, ExternalLink, Calendar, Eye, ChevronLeft, ChevronRight, ImageOff, Image as ImageIcon, Download } from "lucide-react";
import axiosInstance from "../../Api/config";
import { getAllEmployees, getTimestampsApi } from "../../Api/adminApi";

const IST = "Asia/Kolkata";
const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: IST });
const PAGE = 20;

// photo paths like /uploads/x.jpg are served by the same server as the API
const photoUrl = (p) => {
  if (!p) return "";
  if (p.startsWith("http")) return p;
  const base = String(axiosInstance.defaults.baseURL || "").replace(/\/api\/?$/, "");
  return `${base}${p}`;
};

const fmtDay = (t) =>
  new Date(t).toLocaleDateString("en-IN", { timeZone: IST, weekday: "short", day: "2-digit", month: "short", year: "numeric" });
const fmtClock = (t) =>
  new Date(t).toLocaleTimeString("en-IN", { timeZone: IST, hour: "2-digit", minute: "2-digit", hour12: true });

const fmtWhen = (t) =>
  new Date(t).toLocaleString("en-IN", { timeZone: IST, day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true });

export default function Timestamps() {
  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState("");
  const [from, setFrom] = useState(todayIST());
  const [to, setTo] = useState(todayIST());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [showRaw, setShowRaw] = useState(false); // viewer: show the no-watermark copy instead
  const [empQuery, setEmpQuery] = useState(""); // employee dropdown search text
  const [empDropdownOpen, setEmpDropdownOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    getAllEmployees()
      .then((r) => r.success && setEmployees((r.data || []).filter((e) => e.role === "user")))
      .catch(() => {});
  }, []);

  const load = useCallback(
    async (p, append) => {
      setLoading(true);
      setError("");
      try {
        const params = { page: p, limit: PAGE };
        if (userId) params.userId = userId;
        if (from) params.from = from;
        if (to) params.to = to;
        const r = await getTimestampsApi(params);
        if (r.success) {
          setItems((prev) => (append ? [...prev, ...r.data] : r.data));
          setTotal(r.total);
          setPage(p);
        } else setError(r.message || "Could not load photos");
      } catch (e) {
        setError("Could not load photos. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [userId, from, to]
  );

  useEffect(() => { load(1, false); }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const sorted = useMemo(() => [...employees].sort((a, b) => (a.name || "").localeCompare(b.name || "")), [employees]);
  const selectedEmployee = useMemo(() => sorted.find((e) => e._id === userId) || null, [sorted, userId]);
  const filteredEmployees = useMemo(() => {
    const q = empQuery.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter(
      (e) => (e.name || "").toLowerCase().includes(q) || (e.designation || "").toLowerCase().includes(q)
    );
  }, [sorted, empQuery]);
  const setRange = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    setFrom(d.toLocaleDateString("en-CA", { timeZone: IST }));
    setTo(todayIST());
  };

  // A plain <a download> is silently ignored by the browser for a cross-origin URL (the API
  // runs on a different origin than this panel), so it just opens the photo instead of saving
  // it. Fetching the image ourselves and saving it as a blob downloads it for real, every time.
  const downloadPhoto = async () => {
    if (!open || downloading) return;
    const url = photoUrl(showRaw && open.rawPhoto ? open.rawPhoto : open.photo);
    const safeName = String(open.user?.name || "employee").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_");
    const fileName = `timestamp_${safeName}_${open.date || todayIST()}${showRaw && open.rawPhoto ? "_raw" : ""}.jpg`;
    setDownloading(true);
    try {
      const res = await fetch(url);
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (e) {
      window.open(url, "_blank"); // fallback: at least let them save it manually
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="pb-10">
      <div className="mb-6">
        <h1 className="text-3xl sm:text-4xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent flex items-center gap-3">
          <Camera className="text-cyan-600" size={32} /> Timestamp Photos
        </h1>
        <p className="text-gray-500 mt-1">Photos employees stamped with date, time and place. These are separate from attendance.</p>
      </div>

      {/* filters */}
      <div className="bg-white rounded-2xl border border-indigo-100 shadow-sm p-4 sm:p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">Employee</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="All employees"
                value={empDropdownOpen ? empQuery : (selectedEmployee ? `${selectedEmployee.name}${selectedEmployee.designation ? ` - ${selectedEmployee.designation}` : ""}` : "")}
                onChange={(e) => setEmpQuery(e.target.value)}
                onFocus={() => { setEmpQuery(""); setEmpDropdownOpen(true); }}
                onBlur={() => setTimeout(() => setEmpDropdownOpen(false), 150)}
                className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white text-sm focus:outline-none focus:border-cyan-300"
              />
              {empDropdownOpen && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-xl">
                  <button
                    type="button"
                    onMouseDown={(e) => { e.preventDefault(); setUserId(""); setEmpQuery(""); setEmpDropdownOpen(false); }}
                    className="w-full text-left px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-cyan-50"
                  >
                    All employees
                  </button>
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((e) => (
                      <button
                        type="button"
                        key={e._id}
                        onMouseDown={(ev) => { ev.preventDefault(); setUserId(e._id); setEmpQuery(""); setEmpDropdownOpen(false); }}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 ${e._id === userId ? "bg-cyan-50 text-cyan-700 font-semibold" : "text-gray-700"}`}
                      >
                        {e.name}{e.designation ? ` - ${e.designation}` : ""}
                      </button>
                    ))
                  ) : (
                    <p className="px-3 py-2 text-sm text-gray-400">No employee matches</p>
                  )}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">From</label>
            <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-cyan-300" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1">To</label>
            <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-cyan-300" />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Calendar size={14} className="text-gray-400" />
          {[["Today", 0], ["Last 7 days", 6], ["Last 30 days", 29]].map(([label, d]) => (
            <button key={label} onClick={() => setRange(d)} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-indigo-50 text-indigo-700 hover:bg-indigo-100">
              {label}
            </button>
          ))}
          <button onClick={() => { setFrom(""); setTo(""); }} className="px-3 py-1.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 hover:bg-gray-200">
            All dates
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <p className="text-sm text-gray-500">
          Total <span className="font-semibold text-cyan-600">{total}</span> photos
          {total > 0 && <> · Page <span className="font-semibold text-cyan-600">{page}</span> of {totalPages}</>}
        </p>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm p-3">{error}</div>}

      <div className="gradient-card rounded-2xl overflow-hidden shadow-sm">
        <table className="w-full">
          <thead className="bg-gradient-to-r from-indigo-50 to-cyan-50 border-b border-indigo-100">
            <tr>
              {["#", "Photo", "Employee", "Date & Time", "Location", "Note", "Action"].map((h) => (
                <th key={h} className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider last:text-center">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((it, i) => (
              <tr key={it._id} className="table-row-hover">
                <td className="px-4 py-3 text-sm text-gray-500 w-12">{(page - 1) * PAGE + i + 1}</td>
                <td className="px-4 py-3">
                  <button onClick={() => { setShowRaw(false); setOpen(it); }} className="block w-12 h-16 rounded-lg overflow-hidden border border-gray-200 bg-gray-100 hover:ring-2 hover:ring-cyan-300 transition">
                    <img src={photoUrl(it.photo)} alt="timestamp" loading="lazy" className="w-full h-full object-cover" />
                  </button>
                </td>
                <td className="px-4 py-3">
                  <p className="text-sm font-semibold text-gray-900">{it.user?.name || "Employee"}</p>
                  <p className="text-xs text-gray-500">{it.user?.designation || ""}</p>
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <p className="text-sm font-medium text-gray-800">{fmtDay(it.takenAt)}</p>
                  <p className="text-xs text-gray-500">{fmtClock(it.takenAt)}</p>
                </td>
                <td className="px-4 py-3 max-w-xs">
                  {it.address ? <p className="text-sm text-gray-700 line-clamp-2">{it.address}</p> : <p className="text-sm text-gray-400">-</p>}
                  {it.location?.latitude ? (
                    <a
                      className="inline-flex items-center gap-1 text-xs text-cyan-600 font-semibold hover:underline mt-0.5"
                      href={`https://www.google.com/maps?q=${it.location.latitude},${it.location.longitude}`}
                      target="_blank" rel="noreferrer"
                    >
                      <MapPin size={11} /> Map
                    </a>
                  ) : null}
                </td>
                <td className="px-4 py-3 max-w-[14rem]">
                  {it.note ? <p className="text-sm text-indigo-700 line-clamp-2">{it.note}</p> : <p className="text-sm text-gray-400">-</p>}
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => { setShowRaw(false); setOpen(it); }} className="button-hover inline-flex items-center gap-1.5 bg-gradient-to-r from-cyan-500 to-indigo-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold">
                    <Eye size={13} /> View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && items.length === 0 && !error && (
          <div className="text-center py-14">
            <Camera className="mx-auto text-gray-300" size={40} />
            <p className="text-gray-500 mt-3">No timestamp photos for this filter.</p>
          </div>
        )}
        {loading && (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-9 w-9 border-4 border-gray-200 border-t-cyan-500 mx-auto" />
          </div>
        )}

        {total > PAGE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 bg-gray-50/60">
            <button
              onClick={() => load(page - 1, false)}
              disabled={loading || page <= 1}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-gray-200 text-gray-700 disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Previous
            </button>
            <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => load(page + 1, false)}
              disabled={loading || page >= totalPages}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-semibold bg-white border border-gray-200 text-gray-700 disabled:opacity-40"
            >
              Next <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* viewer */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3" onClick={() => setOpen(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] overflow-y-auto lg:flex" onClick={(e) => e.stopPropagation()}>
            <div className="lg:w-3/5 bg-black flex items-center justify-center relative">
              <img
                src={photoUrl(showRaw && open.rawPhoto ? open.rawPhoto : open.photo)}
                alt="timestamp"
                className="max-h-[70vh] lg:max-h-[88vh] w-auto max-w-full object-contain"
              />
              {showRaw && open.rawPhoto ? (
                <span className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-black/60 text-white text-xs font-semibold">No watermark</span>
              ) : null}
            </div>
            <div className="lg:w-2/5 p-5 flex flex-col gap-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-lg font-bold text-gray-900">{open.user?.name}</p>
                  <p className="text-sm text-indigo-600 font-semibold">{open.user?.designation}</p>
                </div>
                <button onClick={() => setOpen(null)} className="text-gray-400 hover:text-gray-600"><X size={22} /></button>
              </div>
              <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm">
                <p className="text-xs font-semibold text-gray-500 uppercase">Taken</p>
                <p className="font-medium text-gray-800">{fmtWhen(open.takenAt)}</p>
              </div>
              {open.note ? (
                <div className="rounded-xl bg-indigo-50 border border-indigo-100 p-3 text-sm">
                  <p className="text-xs font-semibold text-indigo-500 uppercase">Note</p>
                  <p className="text-gray-800">{open.note}</p>
                </div>
              ) : null}
              {open.address || open.location?.latitude ? (
                <div className="rounded-xl bg-gray-50 border border-gray-100 p-3 text-sm">
                  <p className="text-xs font-semibold text-gray-500 uppercase flex items-center gap-1"><MapPin size={12} /> Place</p>
                  {open.address ? <p className="text-gray-800">{open.address}</p> : null}
                  {open.location?.latitude ? (
                    <a
                      className="inline-flex items-center gap-1 text-cyan-600 font-semibold mt-1 hover:underline"
                      href={`https://www.google.com/maps?q=${open.location.latitude},${open.location.longitude}`}
                      target="_blank" rel="noreferrer"
                    >
                      Open in Google Maps <ExternalLink size={13} />
                    </a>
                  ) : null}
                </div>
              ) : null}
              <div className="mt-auto flex flex-col gap-2">
                {open.rawPhoto ? (
                  <button
                    onClick={() => setShowRaw((v) => !v)}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 border-cyan-500 text-cyan-600 text-sm font-semibold hover:bg-cyan-50"
                  >
                    {showRaw ? <><ImageIcon size={15} /> Show with watermark</> : <><ImageOff size={15} /> Remove watermark</>}
                  </button>
                ) : (
                  <p className="text-xs text-gray-400 text-center">No watermark-free copy for this photo (taken before this feature).</p>
                )}
                <button
                  onClick={downloadPhoto}
                  disabled={downloading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-r from-cyan-500 to-indigo-600 text-white text-sm font-semibold disabled:opacity-60"
                >
                  <Download size={15} /> {downloading ? "Downloading…" : "Download Photo"}
                </button>
                <a
                  href={photoUrl(showRaw && open.rawPhoto ? open.rawPhoto : open.photo)}
                  target="_blank" rel="noreferrer"
                  className="text-center px-4 py-2 rounded-xl border border-gray-200 text-gray-600 text-sm font-semibold hover:bg-gray-50"
                >
                  Open full photo
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
